// Los colores del "Fondo liso" — el catálogo, sin UI.
//
// Nació dentro de `components/FondoProductoPicker.tsx`, cuando el liso con color existía
// sólo en Fotos de producto. Desde ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo
// lo leen también Modelo y Catálogo (el mismo control de ajustes del blanco) y el armado
// del payload de los tres modos, así que vive aparte: un catálogo compartido no puede
// colgar del componente de uno de sus consumidores.

/** Colores del "Fondo liso". `blanco` es el default. */
export type ColorFondoLiso = 'blanco' | 'beige' | 'rosa' | 'negro';

/**
 * Swatch de la UI + prompt que viaja como `background_text`.
 *
 * `prompt: null` = blanco, que NO va por descripción sino por el flujo clásico
 * (`background_mode: 'blanco'` + `background_id: 5`): ahí el backend además NORMALIZA el
 * tono del blanco de la imagen generada (ADR-2026-08-14-fondo-blanco-consistente), algo
 * que no existe para los otros colores. Beige/rosa/negro no tienen fila en `fondos`.
 */
export const COLORES_FONDO_LISO: {
  id: ColorFondoLiso;
  label: string;
  hex: string;
  prompt: string | null;
}[] = [
  { id: 'blanco', label: 'Blanco', hex: '#FFFFFF', prompt: null },
  {
    id: 'beige',
    label: 'Beige claro',
    hex: '#EFE5D6',
    prompt:
      'Fondo liso de color beige claro, completamente uniforme, sin texturas ni degradados, con iluminación profesional de estudio fotográfico.',
  },
  {
    id: 'rosa',
    label: 'Rosa claro',
    hex: '#F8E1E7',
    prompt:
      'Fondo liso de color rosa claro pastel, completamente uniforme, sin texturas ni degradados, con iluminación profesional de estudio fotográfico.',
  },
  {
    id: 'negro',
    label: 'Negro',
    hex: '#111111',
    prompt:
      'Fondo liso de color negro, completamente uniforme, sin texturas ni degradados, con iluminación profesional de estudio fotográfico.',
  },
];

/** El prompt del color elegido, o null si es blanco (o no hay color). El armado del
 *  payload lo usa para decidir entre el flujo clásico del blanco y la descripción. */
export const promptDeFondoLiso = (color: ColorFondoLiso | undefined): string | null =>
  COLORES_FONDO_LISO.find((c) => c.id === (color ?? 'blanco'))?.prompt ?? null;

// ─── Ajustes del fondo BLANCO ─────────────────────────────────────────
// Viven acá y no en `FondoPicker.tsx` para que el armado del payload
// (`fondoDelPayload.ts`) sea una función pura sin importar el componente.
// `FondoPicker` los re-exporta: varios los importan de esa ruta.

/** Tono del blanco. NO es una instrucción para la IA: el backend mide el blanco de la
 *  imagen generada y lo lleva a ese objetivo (ADR-2026-08-14-fondo-blanco-consistente). */
export type TemperaturaBlanco = 'frio' | 'neutro' | 'calido';
/** Calidad de la luz sobre el modelo. Esta sí es generativa: va al prompt. */
export type IluminacionBlanco = 'difusa' | 'dura';

export const TEMPERATURA_BLANCO_DEFAULT: TemperaturaBlanco = 'neutro';
export const ILUMINACION_BLANCO_DEFAULT: IluminacionBlanco = 'difusa';

// ─── Cómo se MUESTRA el liso elegido ──────────────────────────────────
// Tres lugares dibujan la opción del liso (la card del panel de Modelo/Catálogo, el tile
// compartido del sheet y la card de `AllFondosView`) y un cuarto la resume en texto. Antes
// los cuatro tenían el swatch blanco y la etiqueta "Fondo blanco" fijos, así que al elegir
// negro decían blanco. La regla vive acá una sola vez
// (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo).

/** El color elegido de una selección, o 'blanco' si no es un liso. */
export const colorDelLiso = (
  sel: { mode: string; colorLiso?: ColorFondoLiso } | null | undefined,
): ColorFondoLiso =>
  sel?.mode === 'blanco' ? (sel.colorLiso ?? 'blanco') : 'blanco';

/** El hex para pintar el swatch. Para el blanco devuelve `#F5F5F5` y no `#FFF`: un
 *  círculo blanco sobre una card blanca no se ve. */
export const hexDelLiso = (
  sel: { mode: string; colorLiso?: ColorFondoLiso } | null | undefined,
): string => {
  const id = colorDelLiso(sel);
  if (id === 'blanco') return '#F5F5F5';
  return COLORES_FONDO_LISO.find((c) => c.id === id)?.hex ?? '#F5F5F5';
};

/** "Fondo blanco" mientras sea blanco; con color, el nombre del color. */
export const etiquetaDelLiso = (
  sel: { mode: string; colorLiso?: ColorFondoLiso } | null | undefined,
): string => {
  const id = colorDelLiso(sel);
  if (id === 'blanco') return 'Fondo blanco';
  return `Fondo ${COLORES_FONDO_LISO.find((c) => c.id === id)?.label.toLowerCase() ?? id}`;
};

/**
 * Qué selección dejar al TOCAR la opción del liso.
 *
 * Re-tocarla no puede perder lo ya elegido: con `{ mode: 'blanco' }` a secas, volver a
 * tocar "Fondo beige" lo reseteaba a blanco (y antes del color ya se comía el tono y la
 * luz). Tres pantallas dibujan esa opción, así que la regla vive acá una sola vez.
 */
export const alElegirLiso = <T extends { mode: string }>(
  sel: T | null | undefined,
): T | { mode: 'blanco' } => (sel?.mode === 'blanco' ? sel : { mode: 'blanco' });
